import { currencyAtom } from "./../../../../../hooks/currencyAtom";
import { useSubmitCheckout } from "@/api/AppApi/services/cart";
import {
  IGetCartResult,
  ISubmitCheckoutResponse,
} from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import {
  adaptFieldsToPost,
  formErrors,
} from "@/app/components/common-form-builder/form-builder-helpers/form-functions";
import { checkoutResultAtom } from "@/hooks/checkoutResultAtom";
import { PagesPaths } from "@/utils/functions/pages-paths";
import { useAtom } from "jotai";
import { useRouter } from "@/app/components/lib/navigation/useRouter";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { getDate } from "@/utils/functions/date-time-functions";
import moment from "moment";
import { useState } from "react";
import { useTrackClickCheckoutEvents } from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/checkout-Events-trackers/useTrackClickCheckoutEvents";
import { selectedShippingMethodAtom } from "@/hooks/shippingMethodAtom";
import { usePayOrder } from "@/api/AppApi/services/order";
import { IGTagUserData } from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/interfaces";
import { toSHA256Hex } from "@/utils/functions/toSHA256Hex";

export const recievingFieldName = "delivery_type";
export const paymentFieldName = "payment";
export const shippingFieldName = "shipping";
export const branchFieldName = "branch_id";
export const addressIdFieldName = "address_id";
export const recievingDateFieldName = "delivery_date";
export const recievingTimeFieldName = "delivery_time";
export const addressNameFieldName = "address(address_name";

export const useCheckoutSubmit = (
  hookForm: UseFormReturn<FieldValues, any>,
  setNotFieldErr: (err: string[]) => void,
) => {
  const router = useRouter();
  const { trackEvents } = useTrackClickCheckoutEvents();
  const [currency] = useAtom(currencyAtom);
  const [, setCheckoutResult] = useAtom(checkoutResultAtom);
  const [selectedShippingMethod] = useAtom(selectedShippingMethodAtom);
  const [error, setError] = useState<string>("");

  const OnSuccess = (res: ISubmitCheckoutResponse) => {
    setCheckoutResult({
      ...res.result,
    });
    if (!res.result.payment_data)
      router.replace(PagesPaths.checkoutResult); //cash
    else router.replace(res.result.payment_data.result); //online
  };

  const OnError = (error: any) => {
    const extraErr = formErrors(error, hookForm);
    const errMsgs =
      !!extraErr && extraErr?.length > 0
        ? extraErr
        : [error?.response?.data?.message?.text];
    if (error?.response?.status === 400) {
      setError(error?.response?.data?.message?.text);
    }

    setNotFieldErr(errMsgs);
  };
  const submitCheckout = useSubmitCheckout({
    onSuccess: OnSuccess,
    onError: OnError,
  });
  const payOrder = usePayOrder({
    onSuccess: OnSuccess,
    onError: OnError,
  });

  const onSubmit = (
    data: FieldValues,
    cart?: IGetCartResult,
    order_id?: number,
  ) => {
    const { payment } = data ?? {};
    const paymentMethod = JSON.parse(payment);
    if (order_id) {
      const dataToSubmit = JSON.parse(payment);
      payOrder.mutate({
        order_id,
        gateway_account_id: dataToSubmit?.id,
        gateway_id: dataToSubmit?.gateway_id,
      });
      return;
    }
    setCheckoutResult({
      payment_data: null, //payment method=cash:null, payment method=online:{...}
      invoice_number: "",
      invoice_id: 0,
      invoice_date: "",
      invoice_time: "",
      amount: 0,
      payment_method: "",
      order_number: "",
      payment_id: "",
      reference_number: "",
      currency,
    });
    const { items, total, tax, coupon, shipping_charge, discount } = cart ?? {};
    const Data: any = {
      ...data,
      delivery_date: data?.[recievingDateFieldName]
        ? getDate(moment(data?.[recievingDateFieldName]).toDate())
        : null,
      delivery_time: data?.[recievingTimeFieldName]
        ? data?.[recievingTimeFieldName]?.split(" - ")?.[0] + ":00"
        : null,
    };
    if (!Data.delivery_date) delete Data.delivery_date;
    if (!Data.delivery_time) delete Data.delivery_time;
    const adaptedFields = adaptFieldsToPost(Data);
    const is_delivery = adaptedFields?.[recievingFieldName] === "delivery";
    delete adaptedFields.payment;
    delete adaptedFields.shipping;
    if (!is_delivery) delete adaptedFields.address;
    if (is_delivery) delete adaptedFields?.[branchFieldName];

    submitCheckout.mutate({ ...adaptedFields, is_delivery });
    const shipping_tier = selectedShippingMethod ?? "pickup";
    const payment_type = paymentMethod?.name ?? "cash";

    const customerData = adaptedFields?.customer;
    const customerPhone =
      data?.["customer(mobile"]?.dial?.dial_code +
      adaptedFields?.customer?.mobile?.number;
    const userdata: IGTagUserData = {
      name: customerData?.name ?? "",
      phone: customerPhone ?? "",
      email: customerData?.email ?? "",
      email_hash: customerData?.email ? toSHA256Hex(customerData?.email) : "",
      phone_hash: customerPhone ? toSHA256Hex(customerPhone) : "",
    };

    trackEvents(
      items ?? [],
      {
        currency: currency?.code,
        value: total,
        coupon: coupon?.code ?? "",
        shipping: shipping_charge,
        tax: tax,
        discount,
        shipping_tier,
        payment_type,
      },
      shipping_tier,
      userdata,
    );
  };
  return {
    onSubmit,
    isLoading: submitCheckout.isLoading || payOrder.isLoading,
    error,
    isSuccess: submitCheckout.isSuccess || payOrder.isSuccess,
  };
};
